import React, { useCallback, useEffect, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { Col, Form, Row, Spin, Typography } from 'antd'
import { includes, isEmpty } from 'lodash'
import PropTypes from 'prop-types'

import {
  DirtyPromptForm,
  FormButtons,
  FormLazyTreeSelect,
} from '../../../common/components/sections'
import { StyledVerticalSpace } from '../../../common/components/styled'
import { PERMISSIONS } from '../../../common/constants'
import { showErrorNotification } from '../../../common/helpers'
import { useFormDirty, useUserHasPermissions } from '../../../common/hooks'
import {
  idPropType,
  simpleEntityPropType,
  treeOptionPropType,
} from '../../../common/prop-types'

import { useUpdateIngredientOverridesIngredientsDetailsMutation } from '../../hooks'
import { setIngredientPageIsDirty } from '../../redux/actions'
import { IngredientConflictingErrors } from '../sections'

import { ingredientOverridesIngredientsDetailsFormInitialValues } from './initial-values'

const IngredientOverridesIngredientsDetailsForm = ({
  ingredientId,
  overridesIngredients,
  ingredientOptions,
  groupedFlatTreeDataByValue,
  flatTreeData,
}) => {
  const dispatch = useDispatch()
  const [form] = Form.useForm()
  const { isDirty, setToDirty, resetForm } = useFormDirty(form)

  const isFormEnabled = useUserHasPermissions([
    PERMISSIONS.INGREDIENT.SAVE_OVERRIDES_INGREDIENTS,
  ])

  const [
    updateIngredientOverridesIngredientsDetails,
    { isLoading: isSaveLoading, error: saveError },
  ] = useUpdateIngredientOverridesIngredientsDetailsMutation()

  const initialValues = useMemo(
    () =>
      ingredientOverridesIngredientsDetailsFormInitialValues(
        overridesIngredients,
      ),
    [overridesIngredients],
  )

  const ingredientIdsRules = useMemo(
    () => [
      () => ({
        validator(_, value) {
          try {
            if (includes(value, ingredientId)) {
              throw new Error(`Can't select owning ingredient`)
            }

            return Promise.resolve()
          } catch (error) {
            return Promise.reject(error)
          }
        },
      }),
    ],
    [ingredientId],
  )

  const handleSaveData = useCallback(
    data => updateIngredientOverridesIngredientsDetails(ingredientId, data),
    [ingredientId, updateIngredientOverridesIngredientsDetails],
  )

  useEffect(() => {
    if (saveError) {
      showErrorNotification({
        title: 'Ingredient details error',
        description: saveError.message,
      })
    }
  }, [saveError])

  useEffect(() => {
    resetForm()
  }, [overridesIngredients, resetForm])

  useEffect(() => {
    dispatch(setIngredientPageIsDirty({ isOverridesIngredientsDirty: isDirty }))
  }, [dispatch, isDirty])

  const { conflicts } = saveError || {}

  return isSaveLoading ? (
    <Spin />
  ) : (
    <DirtyPromptForm
      isDirty={isDirty}
      form={form}
      initialValues={initialValues}
      name="overridesIngredientsForm"
      onFinish={handleSaveData}
      autoComplete="off"
      onFieldsChange={setToDirty}
    >
      <Typography.Title level={3}>Overrides Ingredients</Typography.Title>
      <StyledVerticalSpace>
        <Row>
          <Col span={12}>
            {!isEmpty(conflicts) && (
              <IngredientConflictingErrors errors={conflicts} />
            )}
            <FormLazyTreeSelect
              rules={ingredientIdsRules}
              disabled={!isFormEnabled}
              form={form}
              name="overridesIngredientIds"
              label="Overrides Ingredients"
              showSearch
              labelAbove
              allowClear
              treeCheckable
              treeCheckStrictly
              treeData={ingredientOptions}
              placeholder="Select a new overrides ingredient"
              groupedFlatTreeDataByValue={groupedFlatTreeDataByValue}
              flatTreeData={flatTreeData}
              initialValue={initialValues.overridesIngredientIds}
            />
          </Col>
        </Row>
        <FormButtons
          permissions={[PERMISSIONS.INGREDIENT.SAVE_OVERRIDES_INGREDIENTS]}
          isDisabled={!isDirty}
          onReset={resetForm}
        />
      </StyledVerticalSpace>
    </DirtyPromptForm>
  )
}

IngredientOverridesIngredientsDetailsForm.propTypes = {
  ingredientId: idPropType.isRequired,
  overridesIngredients: PropTypes.arrayOf(simpleEntityPropType),
  ingredientOptions: PropTypes.arrayOf(treeOptionPropType.isRequired)
    .isRequired,
  groupedFlatTreeDataByValue: PropTypes.object.isRequired,
  flatTreeData: PropTypes.arrayOf(PropTypes.object).isRequired,
}

IngredientOverridesIngredientsDetailsForm.defaultProps = {
  overridesIngredients: [],
}

export default IngredientOverridesIngredientsDetailsForm
